import {
  getIcon,
  SLRPrompt,
  SLRPromptLabels,
  SLRSpinner,
  SLRTable,
  showErrorToast,
  TableColumn,
  useAuth
} from '@slr/shared-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  useSetPlatformAdministratorRole,
  useSetPlatformAdminUserEnabled
} from 'hooks/admin/usePlatformAdminUserMutations';
import {
  PlatformAdminRoleFilter,
  useSearchPlatformAdminUsers
} from 'hooks/admin/useSearchPlatformAdminUsers';
import { Empty } from 'pages/organizations';
import {
  ADMINISTRATION_OFFERS_PATH,
  ADMINISTRATION_ORGANIZATIONS_PATH
} from 'configs';
import { FC, FormEvent, useCallback, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  ButtonGroup,
  Col,
  Dropdown,
  DropdownButton,
  Form,
  InputGroup,
  Pagination,
  Row
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router';
import { openAdministrationViewWithSearch } from './administrationNavigation';
import { useAdministrationSearchUrlSync } from './useAdministrationSearchUrlSync';
import { PlatformAdminUser } from 'services/admin/platformAdminUser.types';
import {
  AdministrationOutletContext
} from './AdministrationPage';
import './AdministrationUsersPage.scss';

const PAGE_SIZE_OPTIONS = [1, 10, 25, 50] as const;

const ROLE_FILTER_OPTIONS: PlatformAdminRoleFilter[] = [
  'all',
  'platformAdministrator'
];

const formatDisplayName = (user: PlatformAdminUser) => {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return fullName || user.emailAddress;
};

export const AdministrationUsersPage: FC = () => {
  const { t, i18n } = useTranslation('translation', {
    keyPrefix: 'administrationUsersPage'
  });
  const navigate = useNavigate();
  const { authProfile } = useAuth();
  const {
    usersFilters,
    setUsersFilters,
    setOffersFilters,
    setOrganizationsFilters
  } = useOutletContext<AdministrationOutletContext>();
  const { searchInput, appliedSearch, roleFilter, page, pageSize } =
    usersFilters;
  const { commitSearchToUrl } = useAdministrationSearchUrlSync(setUsersFilters);

  const { data, isPending } = useSearchPlatformAdminUsers(
    appliedSearch,
    page,
    pageSize,
    roleFilter
  );
  const setEnabledMutation = useSetPlatformAdminUserEnabled();
  const setRoleMutation = useSetPlatformAdministratorRole();
  const [userPendingDeactivation, setUserPendingDeactivation] =
    useState<PlatformAdminUser | null>(null);

  const locale = i18n.language === 'en' ? 'en-GB' : 'de-DE';

  const rows = useMemo(() => data?.content ?? [], [data]);

  const closeDeactivationPrompt = useCallback(() => {
    setUserPendingDeactivation(null);
  }, []);

  const handleSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    const trimmedSearch = searchInput.trim();
    setUsersFilters((current) => ({
      ...current,
      page: 0,
      appliedSearch: trimmedSearch
    }));
    commitSearchToUrl(trimmedSearch);
  };

  const handleOpenUserOffers = (emailAddress: string) => {
    openAdministrationViewWithSearch(
      navigate,
      ADMINISTRATION_OFFERS_PATH,
      emailAddress,
      setOffersFilters
    );
  };

  const handleOpenOrganization = (organizationName: string) => {
    openAdministrationViewWithSearch(
      navigate,
      ADMINISTRATION_ORGANIZATIONS_PATH,
      organizationName,
      setOrganizationsFilters
    );
  };

  const handleRoleFilterChange = (nextRoleFilter: PlatformAdminRoleFilter) => {
    setUsersFilters((current) => ({
      ...current,
      page: 0,
      roleFilter: nextRoleFilter
    }));
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    setUsersFilters((current) => ({
      ...current,
      page: 0,
      pageSize: nextPageSize
    }));
  };

  const applyEnabledChange = useCallback(
    async (user: PlatformAdminUser, enabled: boolean) => {
      try {
        await setEnabledMutation.mutateAsync({
          userId: user.userId,
          enabled
        });
      } catch {
      }
    },
    [setEnabledMutation]
  );

  const handleToggleEnabled = (user: PlatformAdminUser) => {
    if (user.enabled && user.userId === authProfile?.id) {
      showErrorToast('toasts.platformAdminUsers.cannotDisableSelf');
      return;
    }
    if (user.enabled) {
      setUserPendingDeactivation(user);
      return;
    }
    void applyEnabledChange(user, true);
  };

  const handleConfirmDeactivation = useCallback(async () => {
    if (!userPendingDeactivation) {
      return;
    }
    try {
      await setEnabledMutation.mutateAsync({
        userId: userPendingDeactivation.userId,
        enabled: false
      });
      closeDeactivationPrompt();
    } catch {
    }
  }, [
    closeDeactivationPrompt,
    setEnabledMutation,
    userPendingDeactivation
  ]);

  const handleToggleAdminRole = async (user: PlatformAdminUser) => {
    if (
      user.platformAdministrator &&
      user.userId === authProfile?.id
    ) {
      showErrorToast('toasts.platformAdminUsers.cannotRevokeOwnAdminRole');
      return;
    }
    try {
      await setRoleMutation.mutateAsync({
        userId: user.userId,
        granted: !user.platformAdministrator
      });
    } catch {
    }
  };

  const columns: TableColumn<PlatformAdminUser>[] = useMemo(
    () => [
      {
        label: t('columns.status'),
        accessor: 'enabled',
        cellFn: ({ data: user }) => (
          <Badge
            bg={user.enabled ? 'success' : 'secondary'}
            title={user.enabled ? t('status.active') : t('status.disabled')}
            aria-label={
              user.enabled ? t('status.active') : t('status.disabled')
            }
          >
            <FontAwesomeIcon
              icon={getIcon('fas', user.enabled ? 'check' : 'times')}
            />
          </Badge>
        )
      },
      {
        label: t('columns.name'),
        accessor: 'firstName',
        cellFn: ({ data: user }) => (
          <Button
            type="button"
            variant="link"
            className="administration-cross-link"
            onClick={() => handleOpenUserOffers(user.emailAddress)}
            aria-label={t('openUserOffers', { name: formatDisplayName(user) })}
            title={user.emailAddress}
          >
            {formatDisplayName(user)}
          </Button>
        )
      },
      {
        label: t('columns.email'),
        accessor: 'emailAddress'
      },
      {
        label: t('columns.administrator'),
        accessor: 'platformAdministrator',
        cellFn: ({ data: user }) => (
          <Badge bg={user.platformAdministrator ? 'danger' : 'primary'}>
            {user.platformAdministrator
              ? t('administrator.yes')
              : t('administrator.no')}
          </Badge>
        )
      },
      {
        label: t('columns.accountType'),
        accessor: 'accountType',
        cellFn: ({ data: user }) =>
          user.accountType === 'business_account'
            ? t('accountType.business')
            : t('accountType.private')
      },
      {
        label: t('columns.organizations'),
        accessor: 'organizations',
        cellFn: ({ data: user }) =>
          user.organizations.length === 0 ? (
            t('noOrganizations')
          ) : (
            <div className="administration-users-page__organizations">
              {user.organizations.map((organization, index) => (
                <span key={organization.organizationId}>
                  <Button
                    type="button"
                    variant="link"
                    className="administration-cross-link"
                    onClick={() =>
                      handleOpenOrganization(organization.organizationName)
                    }
                    aria-label={t('openOrganization', {
                      name: organization.organizationName
                    })}
                    title={organization.organizationName}
                  >
                    {organization.organizationName}
                  </Button>
                  {index < user.organizations.length - 1 ? ', ' : null}
                </span>
              ))}
            </div>
          )
      },
      {
        label: t('columns.lastLogin'),
        accessor: 'lastLoginTimestamp',
        cellFn: ({ data: user }) =>
          user.lastLoginTimestamp
            ? new Date(user.lastLoginTimestamp).toLocaleString(locale)
            : t('neverLoggedIn')
      },
      {
        label: t('columns.actions'),
        accessor: 'userId',
        cellFn: ({ data: user }) => (
          <DropdownButton
            as={ButtonGroup}
            size="sm"
            variant="outline-primary"
            title={t('actions.label')}
            disabled={
              setEnabledMutation.isPending || setRoleMutation.isPending
            }
          >
            <Dropdown.Item onClick={() => handleToggleEnabled(user)}>
              {user.enabled ? t('actions.disable') : t('actions.enable')}
            </Dropdown.Item>
            {!(
              user.platformAdministrator && user.userId === authProfile?.id
            ) && (
                <Dropdown.Item onClick={() => handleToggleAdminRole(user)}>
                  {user.platformAdministrator
                    ? t('actions.revokeAdmin')
                    : t('actions.grantAdmin')}
                </Dropdown.Item>
              )}
          </DropdownButton>
        )
      }
    ],
    [t, locale, setEnabledMutation.isPending, setRoleMutation.isPending, authProfile?.id]
  );

  const totalPages = Math.max(data?.totalPages ?? 0, 1);

  return (
    <div className="administration-users-page">
      {userPendingDeactivation && (
        <SLRPrompt
          isLoading={setEnabledMutation.isPending}
          confirmButtonVariant="danger"
          labels={
            t('disableConfirmation', {
              returnObjects: true,
              name: formatDisplayName(userPendingDeactivation)
            }) as SLRPromptLabels
          }
          onConfirm={() => {
            void handleConfirmDeactivation();
          }}
          onCancel={closeDeactivationPrompt}
        />
      )}
      <Row className="administration-toolbar align-items-center justify-content-center g-3 mb-4">
        <Col xs={12} sm={8} md={6} lg={4} className="administration-toolbar__search">
          <Form onSubmit={handleSearchSubmit}>
            <InputGroup>
              <Form.Control
                type="search"
                value={searchInput}
                onChange={(event) => {
                  const nextSearchInput = event.target.value;
                  setUsersFilters((current) => {
                    if (
                      nextSearchInput === '' &&
                      current.appliedSearch !== ''
                    ) {
                      commitSearchToUrl('');
                      return {
                        ...current,
                        searchInput: nextSearchInput,
                        appliedSearch: '',
                        page: 0
                      };
                    }
                    return {
                      ...current,
                      searchInput: nextSearchInput
                    };
                  });
                }}
                placeholder={t('searchPlaceholder')}
                aria-label={t('searchPlaceholder')}
              />
              <Button type="submit" variant="primary">
                {t('search')}
              </Button>
            </InputGroup>
          </Form>
        </Col>
        <Col xs="auto">
          <Form.Group
            controlId="administration-users-role-filter"
            className="administration-users-page__filter d-flex align-items-center gap-2"
          >
            <Form.Label className="mb-0 text-nowrap">
              {t('roleFilter.label')}
            </Form.Label>
            <Form.Select
              value={roleFilter}
              onChange={(event) =>
                handleRoleFilterChange(
                  event.target.value as PlatformAdminRoleFilter
                )
              }
            >
              {ROLE_FILTER_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {t(`roleFilter.${option}`)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs="auto">
          <Form.Group
            controlId="administration-users-page-size"
            className="administration-users-page__page-size d-flex align-items-center gap-2"
          >
            <Form.Label className="mb-0 text-nowrap">
              {t('pageSize')}
            </Form.Label>
            <Form.Select
              value={pageSize}
              onChange={(event) =>
                handlePageSizeChange(Number(event.target.value))
              }
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {isPending ? (
        <SLRSpinner />
      ) : (
        <>
          <SLRTable<PlatformAdminUser>
            emptyView={
              <Empty
                labels={
                  t('empty', { returnObjects: true }) as {
                    top: string;
                    bottom: string;
                  }
                }
              />
            }
            sortIcon={getIcon('fas', 'long-arrow-up')}
            columns={columns}
            data={rows}
          />

          <div className="d-flex justify-content-center mt-4">
            <Pagination>
              <Pagination.Prev
                disabled={page <= 0}
                onClick={() =>
                  setUsersFilters((current) => ({
                    ...current,
                    page: Math.max(0, current.page - 1)
                  }))
                }
              />
              {Array.from({ length: totalPages }, (_, index) => (
                <Pagination.Item
                  key={index}
                  active={index === page}
                  onClick={() =>
                    setUsersFilters((current) => ({
                      ...current,
                      page: index
                    }))
                  }
                >
                  {index + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                disabled={page >= totalPages - 1}
                onClick={() =>
                  setUsersFilters((current) => ({
                    ...current,
                    page: Math.min(totalPages - 1, current.page + 1)
                  }))
                }
              />
            </Pagination>
          </div>
        </>
      )}
    </div>
  );
};
